import { supabase } from './supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email: string;
  avatar_url?: string;
  full_name?: string;
}

export interface UserProfile {
  id: string;
  full_name: string;
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  user: User | null;
  error: AuthError | null;
}

export interface ProfileResponse {
  profile: UserProfile | null;
  error: Error | null;
}

// Auth service for managing authentication
export const authService = {
  // Get current user
  async getCurrentUser(): Promise<{ user: User | null; session: Session | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      // If there's an auth session missing error, that's normal for non-authenticated users
      if (userError && userError.message === 'Auth session missing!') {
        return { user: null, session: null };
      }
      
      if (userError) throw userError;
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      
      return { user, session };
    } catch (error) {
      // Handle auth session missing error gracefully
      if (error && (error as AuthError).message === 'Auth session missing!') {
        return { user: null, session: null };
      }
      console.error('Auth error:', error);
      return { user: null, session: null };
    }
  },

  // Sign up with email and password
  async signUp(email: string, password: string, fullName?: string): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          full_name: fullName || ''
        }
      }
    });
    return { user: data.user, error };
  },

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { user: data.user, error };
  },

  // Sign in with Google OAuth
  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { error };
  },

  // Sign out
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Reset password
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  },

  // Update password
  async updatePassword(password: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password,
    });
    return { error };
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user || null, session);
    });
  }
};

// User profile service
export const userProfileService = {
  // Create user profile
  async createProfile(userId: string, fullName: string): Promise<ProfileResponse> {
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          id: userId,
          full_name: fullName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();
    
    return { profile: data, error: error ? new Error(error.message) : null };
  },

  // Get user profile
  async getProfile(userId: string): Promise<ProfileResponse> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle(); // maybeSingle: profil yoksa null döner, hata fırlatmaz

    return { profile: data, error: error ? new Error(error.message) : null };
  },

  // Update user profile
  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<ProfileResponse> {
    const { data, error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();
    
    return { profile: data, error: error ? new Error(error.message) : null };
  },

  // Get or create profile (useful for first-time login)
  async getOrCreateProfile(userId: string, fullName?: string): Promise<ProfileResponse> {
    // Try to get existing profile
    const { profile, error } = await this.getProfile(userId);
    
    if (profile) {
      return { profile, error: null };
    }
    
    // If profile doesn't exist and we have a name, create it
    if (!profile && fullName) {
      return await this.createProfile(userId, fullName);
    }
    
    // Return error if no profile and no name provided
    return { profile: null, error: error || new Error('Profile not found and no name provided') };
  }
};

interface Favorite {
  id: string;
  created_at: string;
  videos: {
    id: string;
    title: string;
    description?: string;
    video_url: string;
    thumbnail_url?: string;
    slug?: string;
    products: Array<{
      id?: string;
      name: string;
      price: string;
      link?: string;
      trendyol_link?: string;
      image_url?: string;
    }>;
    tags: string[];
    like_count: number;
    created_at: string;
  };
}

// Favorites service for managing user favorites
export const favoritesService = {
  // Add video to favorites
  async addFavorite(userId: string, videoId: string): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from('favorites')
      .insert([{ user_id: userId, video_id: videoId }]);
    return { error: error ? new Error(error.message) : null };
  },

  // Remove video from favorites
  async removeFavorite(userId: string, videoId: string): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('user_id', userId)
      .eq('video_id', videoId);
    return { error: error ? new Error(error.message) : null };
  },

  // Get user's favorite videos
  async getUserFavorites(userId: string): Promise<{ favorites: Favorite[]; error: Error | null }> {
    const { data, error } = await supabase
      .from('favorites')
      .select(`
        id,
        created_at,
        videos (
          id,
          title,
          description,
          video_url,
          thumbnail_url,
          slug,
          products,
          tags,
          like_count,
          created_at
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    return { favorites: (data as Favorite[]) || [], error: error ? new Error(error.message) : null };
  },

  // Check if video is favorited by user
  async isFavorited(userId: string, videoId: string): Promise<{ isFavorited: boolean; error: Error | null }> {
    const { data, error } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('video_id', videoId)
      .maybeSingle(); // maybeSingle: veri yoksa null döner, hata fırlatmaz

    return { isFavorited: !!data, error: error ? new Error(error.message) : null };
  },

  // Get like count for a video
  async getLikeCount(videoId: string): Promise<{ count: number; error: Error | null }> {
    const { data, error } = await supabase
      .from('videos')
      .select('like_count')
      .eq('id', videoId)
      .single();
    
    return { count: data?.like_count || 0, error: error ? new Error(error.message) : null };
  },

  // Get total favorites count for a user
  async getUserFavoritesCount(userId: string): Promise<{ count: number; error: Error | null }> {
    const { count, error } = await supabase
      .from('favorites')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    return { count: count || 0, error: error ? new Error(error.message) : null };
  }
};
